// src/pages/championships/ChampionshipList.jsx

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import championshipService from '../../services/championshipService';
import useAuth from '../../hooks/useAuth';
import './ChampionshipList.css';

/* ---------- Ícones ---------- */
const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const SearchIcon = () => (
  <svg {...iconProps}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);
const PlusIcon = () => (
  <svg {...iconProps}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const PinIcon = () => (
  <svg {...iconProps}>
    <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const TrophyIcon = () => (
  <svg {...iconProps}>
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" />
    <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
  </svg>
);

/* ---------- Logo (com iniciais como fallback) ---------- */
const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join('');

const ChampionshipLogo = ({ logo, name }) => {
  const [failed, setFailed] = useState(false);
  const showImage = logo && !failed;

  return (
    <div className="championship-logo">
      {showImage ? (
        <img src={logo} alt={`Logo de ${name}`} onError={() => setFailed(true)} />
      ) : (
        <span className="championship-logo-initials" aria-hidden="true">
          {getInitials(name)}
        </span>
      )}
    </div>
  );
};

/* ---------- Card ---------- */
const ChampionshipCard = ({ champ, handleDelete }) => {
  const categoryDisplay = useMemo(() => {
    if (!champ) return null;
    const { gender, baseCategory, customCategory } = champ;
    const parts = [];
    if (gender) parts.push(gender);
    if (baseCategory) {
      parts.push(baseCategory);
    } else if (customCategory) {
      parts.push(customCategory);
    }
    return parts.join(' - ');
  }, [champ]);

  return (
    <article className="championship-card">
      <div className="championship-card-top">
        <ChampionshipLogo logo={champ.logo} name={champ.name} />
        <div className="championship-card-title">
          <h3>{champ.name}</h3>
          {categoryDisplay && <span className="championship-category">{categoryDisplay}</span>}
        </div>
      </div>

      <div className="championship-info">
        <ul className="championship-meta">
          {champ.location && (
            <li>
              <PinIcon />
              <span>{champ.location}</span>
            </li>
          )}
          {champ.format && (
            <li>
              <TrophyIcon />
              <span>{champ.format}</span>
            </li>
          )}
        </ul>
      </div>

      <div className="championship-actions">
        <Link to={`/championships/${champ._id}`} className="button details">Ver detalhes</Link>
        {handleDelete && (
          <>
            <Link to={`/championships/${champ._id}/edit`} className="button edit">Editar</Link>
            <button
              type="button"
              onClick={() => handleDelete(champ._id)}
              className="button delete"
            >
              Excluir
            </button>
          </>
        )}
      </div>
    </article>
  );
};

/* ---------- Seção ---------- */
const ChampionshipSection = ({ title, items, handleDelete }) => (
  <section className="championship-section">
    <h2 className="championship-section-title">
      {title}
      <span className="championship-count">{items.length}</span>
    </h2>
    <div className="championship-grid">
      {items.map(champ => (
        <ChampionshipCard key={champ._id} champ={champ} handleDelete={handleDelete} />
      ))}
    </div>
  </section>
);

/* ---------- Página ---------- */
function ChampionshipList() {
  const { user, loadingAuth } = useAuth();

  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchChampionships();
  }, []);

  const fetchChampionships = () => {
    championshipService.getAll()
      .then(response => setChampionships(response.data.data || response.data))
      .catch(error => console.error('Erro ao buscar campeonatos:', error))
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este campeonato?')) return;
    championshipService.delete(id)
      .then(() => fetchChampionships())
      .catch(error => console.error('Erro ao deletar campeonato:', error));
  };

  const { myChampionships, otherChampionships } = useMemo(() => {
    if (loadingAuth || !user) {
      return { myChampionships: [], otherChampionships: championships };
    }
    const userId = user._id;
    const mine = championships.filter(champ =>
      champ.creator?._id === userId ||
      champ.managers?.some(m => m._id === userId)
    );
    const others = championships.filter(champ => !mine.includes(champ));
    return { myChampionships: mine, otherChampionships: others };
  }, [championships, user, loadingAuth]);

  const applyFilters = (list) => {
    let filtered = [...list];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(champ =>
        champ.name?.toLowerCase().includes(term) ||
        champ.location?.toLowerCase().includes(term)
      );
    }
    return filtered.sort((a, b) =>
      sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    );
  };

  if (loading || loadingAuth) {
    return <div className="loading-state">Carregando campeonatos...</div>;
  }

  const canCreate = user?.role === 'Admin' || user?.role === 'Gestor';
  const visibleMine = applyFilters(myChampionships);
  const visibleOthers = applyFilters(otherChampionships);
  const hasAny = championships.length > 0;
  const hasResults = visibleMine.length + visibleOthers.length > 0;

  return (
    <div className="championship-list-container">
      <header className="championship-list-header">
        <div>
          <h1>Campeonatos</h1>
          <p className="championship-list-subtitle">
            {championships.length === 1
              ? '1 campeonato cadastrado'
              : `${championships.length} campeonatos cadastrados`}
          </p>
        </div>

        {canCreate && (
          <Link to="/championships/new" className="new-championship-button">
            <PlusIcon />
            Novo campeonato
          </Link>
        )}
      </header>

      <div className="filters-container">
        <div className="filter-field">
          <SearchIcon />
          <input
            type="text"
            placeholder="Buscar por nome ou cidade"
            aria-label="Buscar campeonatos por nome ou cidade"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="sort-buttons" role="group" aria-label="Ordenar por nome">
          <button
            type="button"
            className={sortOrder === 'asc' ? 'active' : ''}
            aria-pressed={sortOrder === 'asc'}
            onClick={() => setSortOrder('asc')}
          >A-Z</button>
          <button
            type="button"
            className={sortOrder === 'desc' ? 'active' : ''}
            aria-pressed={sortOrder === 'desc'}
            onClick={() => setSortOrder('desc')}
          >Z-A</button>
        </div>
      </div>

      {!hasAny && (
        <div className="no-championships">
          <p>Nenhum campeonato cadastrado ainda.</p>
          {canCreate && <Link to="/championships/new">Cadastrar o primeiro campeonato</Link>}
        </div>
      )}

      {hasAny && !hasResults && (
        <div className="no-championships">
          <p>Nenhum campeonato encontrado para “{searchTerm}”.</p>
          <p>Tente buscar por outro nome ou cidade.</p>
        </div>
      )}

      {visibleMine.length > 0 && (
        <ChampionshipSection
          title="Meus campeonatos"
          items={visibleMine}
          handleDelete={handleDelete}
        />
      )}

      {visibleOthers.length > 0 && (
        <ChampionshipSection
          title={myChampionships.length > 0 ? 'Outros campeonatos' : 'Todos os campeonatos'}
          items={visibleOthers}
        />
      )}
    </div>
  );
}

export default ChampionshipList;
